<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid job-info-container">
        <v-card class="p-b-40 p-t-30 p-l-30 p-r-30 m-b-40">
          <div class="el-loading-mask" v-if="loading">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <!-- Name -->
          <div class="row">
            <div class="col-md-8 header">
              <h3 class="title-7 p-b-10">
                <v-btn
                  text
                  icon
                  class="m-0"
                  @click="goToList()">
                  <i class="zmdi zmdi-long-arrow-left"></i>
                </v-btn>
                {{ text('dvr_record_header') }}: {{ record.name }}
                <v-btn
                  text
                  icon
                  class="m-0"
                  @click.stop="editDialog = true">
                  <i class="zmdi zmdi-edit grey--text" style="font-size: 1.4rem;"></i>
                </v-btn>
              </h3>
            </div>
            <div class="col-md-4 text-right">
              {{ text('current_utc_time') }}: {{ currentT }}
            </div>
          </div>
          <v-divider></v-divider>
          <div class="row m-b-20">
            <div class="col-md-4 col-sm-12" style="min-width: 630px; width: 630px;">
              <iframe id="on_air"
                      :src="frameSrc"
                      height="380"
                      width="630"
                      frameborder="0">
              </iframe>
            </div>
            <div class="col-md-5 col-sm-12 p-l-30">
              <div class="row">
                <div class="col-md-12 p-b-20 p-t-10">
                  <h4>
                    <v-icon class="p-r-5" size="18">fa fa-play</v-icon>
                    <span v-if="record.state === 'error'">Live</span>
                    <span v-else class="p-l-5">
                      {{ playFrom.start }} {{ playFrom.duration }}
                    </span>
                  </h4>
                </div>
              </div>
              <div class="row">
                <div class="col-md-4">
                  <v-select
                    @change="jumpToChange()"
                    v-model="jumpTo"
                    :label="text('jump_to_label')"
                    :items="jumps">
                  </v-select>
                </div>
                <div class="col-md-7 p-l-0">
                  <v-btn
                    :loading="loading"
                    color="primary"
                    class="ma-3 white--text"
                    @click="periodDialog = true">
                    <v-icon class="p-r-10">fa fa-calendar</v-icon>
                    {{ text('play_from_btn') }}
                  </v-btn>
                </div>
              </div>
              <div class="row">
                <div class="col col-md-4 p-t-15 subtitle-2">
                  {{ text('period') }}:
                </div>
                <div class="col col-md-8 p-t-15">
                  {{ recordPeriod }}
                </div>
              </div>
              <div class="row" v-if="record.state === 'ok'">
                <div class="col col-md-4 p-t-15 subtitle-2">
                  {{ text('earliest_recording') }}:
                </div>
                <div class="col col-md-8 p-t-15">
                  {{ earliestStr }}
                </div>
              </div>
              <div class="row">
                <div class="col col-md-4 p-t-15 subtitle-2">
                  {{ text('state') }}:
                </div>
                <div class="col col-md-8 p-t-15">
                  <span class="text-uppercase blocked-text lighten-4"
                        :class="{'green': record.state === 'ok', 'red': record.state === 'error'}">
                    {{ record.state }}
                  </span>
                </div>
              </div>
              <div class="row" v-if="record.error">
                <div class="col col-md-4 p-t-15 subtitle-2">
                  {{ text('error') }}:
                </div>
                <div class="col col-md-8 p-t-15">
                  <span class="red--text lighten-4">
                    {{ record.error }}
                  </span>
                </div>
              </div>
              <div class="row" v-if="channel.id">
                <div class="col col-md-4 p-t-15 subtitle-2">
                  {{ text('channel') }}:
                </div>
                <div class="col col-md-8 p-t-15 truncate-long-text">
                  <a href="#" @click.prevent="goToChannel()">{{ channel.name }}</a>
                </div>
              </div>
              <div class="row" v-if="recordingBitrates">
                <div class="col col-md-4 p-t-15 subtitle-2">
                  {{ text('recording_bitrates') }}:
                </div>
                <div class="col col-md-8 p-t-15 truncate-long-text">
                  {{ recordingBitrates }}
                </div>
              </div>
              <div class="row">
                <div class="col col-md-4 p-t-15 subtitle-2">
                  {{ text('input_url') }}:
                </div>
                <div class="col col-md-8 p-t-15 truncate-long-text">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <a href="#" @click.prevent="setLive()" v-bind="attrs" v-on="on">
                        {{ record.inputURL }}
                      </a>
                    </template>
                    {{ record.inputURL }}
                  </v-tooltip>
                </div>
              </div>
              <div class="row">
                <div class="col col-md-4 p-t-10 grey--text subtitle-2">
                  ID:
                </div>
                <div class="col col-md-8 p-t-10 grey--text">
                  {{ record.id }}
                </div>
              </div>
              <div class="row">
                <div class="col col-md-4 p-t-15 grey--text subtitle-2">
                  {{ text('created') }}:
                </div>
                <div class="col col-md-8 p-t-15 grey--text">
                  {{ formatTime(record.createTime) }}
                </div>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <div class="row m-t-20 m-l-0 m-r-0">
            <div class="col-md-12 p-b-15 p-l-0">
              <h4>
                <v-icon class="p-r-10" size="18">fa fa-download</v-icon>
                <span v-if="download.start">
                  {{ download.start }} - {{ download.end }}
                </span> UTC
              </h4>
            </div>
            <!--div class="col-md-2 p-l-20">
              <v-select
                hide-details
                outline
                v-model="format"
                label="Format"
                @change="formatChange()"
                :items="formats">
              </v-select>
            </div-->
            <div class="col-md-12 p-l-15 p-r-15 p-t-15 p-b-15"
                 style="border: 2px solid grey; border-radius: 3px;">
              <i class="fa fa-link m-r-10 m-l-5"></i>
              <a :href="mp4URL" @click.prevent="downloadFile()">
                {{ mp4URL }}
              </a>
              <i @click="handleCopy(mp4URL)"
                 class="zmdi zmdi-copy m-l-10 pointer"></i>
            </div>
          </div>
        </v-card>
      </div>
    </div>
    <!-- Period Dialog -->
    <v-dialog v-model="periodDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">
          Play from
        </v-card-title>
        <v-card-text>
          <div class="row">
            <div class="col-md-6">
              <div class="row">
                <div class="col-12 p-r-30">
                  <v-menu
                    hide-details
                    v-model="period.dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="period.date"
                        label="Start date"
                        prepend-icon="event"
                        class="p-l-15"
                        v-bind="attrs"
                        v-on="on">
                      </v-text-field>
                    </template>
                    <v-date-picker
                      @change="startDateChange"
                      v-model="period.date"
                      no-title
                      @input="period.dateMenu = false">
                    </v-date-picker>
                  </v-menu>
                </div>
                <div class="col-12 p-l-60 p-r-30">
                  <v-text-field
                    @change="startDateChange"
                    v-model="period.time"
                    label="Start time"
                    type="time"
                    suffix="UTC">
                  </v-text-field>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="col-12">
                <v-menu
                  hide-details
                  v-model="period.endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="period.endDate"
                      label="End date"
                      prepend-icon="event"
                      v-bind="attrs"
                      v-on="on">
                    </v-text-field>
                  </template>
                  <v-date-picker
                    @change="endDateChange"
                    v-model="period.endDate"
                    no-title
                    @input="period.endDateMenu = false">
                  </v-date-picker>
                </v-menu>
              </div>
              <div class="col-12 p-l-45">
                <v-text-field
                  @change="endDateChange"
                  v-model="period.endTime"
                  label="End time"
                  type="time"
                  suffix="UTC">
                </v-text-field>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12 text-center">
              Play time: {{ playTime }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn depressed small text
                 @click="periodDialog = false">Cancel</v-btn>
          <v-btn
            :disabled="playTime === 'Invalid Period'"
            depressed small color="primary"
                 @click="applyPeriod()">Play</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="500" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline">
          Channel Recording Settings
        </v-card-title>
        <v-card-text>
          <div class="row">
            <div class="col-12">
              <v-text-field
                v-model="form.name"
                label="Name"
                :counter="255"
                :rules="rules.nameRules"
                required>
              </v-text-field>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <v-text-field
                v-model="form.duration.days"
                type="number"
                label="Record duration"
                @change="dayPeriodChanged"
                suffix="days">
              </v-text-field>
            </div>
            <div class="col-md-6">
              <v-text-field
                @change="hourPeriodChanged"
                v-model="form.duration.hours"
                type="number"
                suffix="hours">
              </v-text-field>
            </div>
          </div>
          <div class="row" v-if="limit > 0 && !isPeriodValid">
            <div class="col-md-12 error--text body-1">
              The limit is exceeded. Your limit: {{ limitStr }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn depressed small text
                 @click="cancelEditing()">Cancel</v-btn>
          <v-btn depressed small color="primary"
                 @click="updateRecord()">Update</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import { setClipboard } from '@/helpers/utils';
import { processDVRChannel, DVRChannelFormToJsonAPI } from '@/store/converters';

export default {
  created() {
    this.updateTimer();

    const recordId = this.$route.params.id;
    this.setFrameSrc('');

    this.$store.commit('setPageLoader');
    this.$store.dispatch('dvr/getDVRChannel', recordId)
      .then((dc) => {
        this.record = processDVRChannel(dc);
        this.setEditForm();
        this.updateResultURL();
        this.findEarliest();
        this.setPeriodTimes();
        this.findChannelName();
        if (moment.duration(moment().diff(moment(this.record.createTime))).asMinutes() < 3) {
          this.setLive();
        }
      })
      .catch((errorMsg) => {
        this.$toasted.error(errorMsg, {
          theme: 'toasted-primary',
          position: 'top-center',
          duration: 10000,
        });
        this.$router.push({ name: 'DVRListPage' });
      })
      .finally(() => this.$store.commit('resolvePageLoader'));
  },
  data() {
    return {
      currentT: '',
      record: {},
      frameSrc: '',
      jumpTo: '-1 hour',
      jumps: ['-1 hour', '-5 hours', '-1 day'],
      format: 'HLS3',
      formats: ['HLS3', 'HLS5', 'HLS7', 'DASH'],
      periodDialog: false,
      period: {
        date: '',
        dateMenu: false,
        time: '',
        endDate: '',
        endDateMenu: false,
        endTime: '',
      },
      origin: 'now',
      offset: '-60m',
      resultURL: '',
      mp4URL: '',
      earliest: '',
      earliestStr: '',
      channel: {},
      recordingBitrates: '',
      download: {
        start: '',
        end: '',
      },
      playFrom: {
        start: '',
        duration: '',
      },
      editDialog: false,
      form: {
        name: '',
        duration: {
          days: 0,
          hours: 0,
        },
      },
      rules: {
        nameRules: [
          v => !!v || 'Name is required',
          v => (v.length >= 3 && v.length <= 255) || 'Name must be between 3 and 255 characters',
        ],
      },
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    recordPeriod() {
      const duration = moment.duration(this.record.duration * 1000);
      let days = Math.floor(duration.asDays());
      if (days < 1) {
        days = 0;
        return `${duration.hours()}h`;
      }
      duration.subtract(moment.duration(days, 'days'));
      const hours = duration.hours();
      return `${days}d ${hours}h`;
    },
    user() {
      return this.$store.getters.user || {};
    },
    limit() {
      let limit = 0;
      if (this.user && this.user.account && this.user.account.limits) {
        limit = this.user.account.limits.dvr_channel_duration_total;
      }
      return limit;
    },
    limitStr() {
      let limit = 0;
      if (this.user && this.user.account && this.user.account.limits) {
        limit = this.user.account.limits.dvr_channel_duration_total;
      }
      const duration = moment.duration(limit * 1000);
      let days = Math.floor(duration.asDays());
      if (days < 1) {
        days = 0;
      }
      duration.subtract(moment.duration(days, 'days'));
      const hours = duration.hours();
      return `${days}d ${hours}h`;
    },
    isPeriodValid() {
      const dur = Number(this.form.duration.days) * 86400 + Number(this.form.duration.hours) * 3600;
      return dur <= this.limit;
    },
    isFormValid() {
      if (!(this.form.name.length >= 3 && this.form.name.length <= 255)) {
        return false;
      }
      const dur = Number(this.form.duration.days) * 86400 + Number(this.form.duration.hours) * 3600;
      if (dur === 0) {
        return false;
      }
      return this.isPeriodValid;
    },
    playTime() {
      const start = new Date(`${this.period.date}T${this.period.time}`).getTime();
      const end = new Date(`${this.period.endDate}T${this.period.endTime}`).getTime();
      const diff = end - start;
      if (diff <= 0) {
        return 'Invalid Period';
      }
      const duration = moment.duration(diff);
      let days = Math.floor(duration.asDays());
      if (days < 1) {
        days = 0;
      }
      duration.subtract(moment.duration(days, 'days'));
      let hours = Math.floor(duration.hours());
      if (hours < 1) {
        hours = 0;
      }
      duration.subtract(moment.duration(hours, 'hours'));
      const minutes = Math.floor(duration.minutes());
      if (days > 0) {
        return `${days}d ${hours}h ${minutes}m`;
      } if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    updateTimer() {
      setInterval(() => {
        this.currentT = moment().utc().format('DD/MM/YYYY HH:mm:ss');
        if (!this.periodDialog) {
          this.findEarliest();
        }
      }, 1000);
    },
    setEditForm() {
      this.form.name = this.record.name;
      const duration = moment.duration(this.record.duration * 1000);
      let days = Math.floor(duration.asDays());
      if (days < 1) {
        days = 0;
      }
      duration.subtract(moment.duration(days, 'days'));
      this.form.duration.days = days;
      this.form.duration.hours = duration.hours();
    },
    setFrameSrc(url) {
      this.frameSrc = `/js/hlsjs/index2.html?src=${url}`;
    },
    formatTime(time) {
      return `${moment(time).utc().format('DD/MM/YYYY HH:mm')} UTC`;
    },
    handleCopy(text) {
      setClipboard(text);
      this.$toasted.info(this.text('copied'), {
        theme: 'toasted-primary',
        position: 'top-center',
        duration: 1000,
      });
    },
    findEarliest() {
      const start = moment().add(-this.record.duration, 'seconds').unix();
      const created = moment(this.record.createTime).unix();
      this.earliest = Math.max(start, created);
      this.earliestStr = `${moment.unix(this.earliest).utc().format('DD/MM/YYYY HH:mm')} UTC`;
    },
    setPeriodTimes() {
      this.period.date = moment.unix(this.earliest).utc().format('YYYY-MM-DD');
      this.period.time = moment.unix(this.earliest).utc().format('HH:mm');
      this.period.endDate = moment().utc().format('YYYY-MM-DD');
      this.period.endTime = moment().utc().format('HH:mm');
    },
    applyPeriod() {
      this.jumpTo = '';
      const t = new Date(`${this.period.date}T${this.period.time}`).getTime() / 1000;
      this.origin = moment.unix(t).format('YYYYMMDDHHmm');

      const end = new Date(`${this.period.endDate}T${this.period.endTime}`).getTime() / 1000;
      const minutes = (end - t) / 60;
      this.offset = `${minutes}m`;

      this.updateResultURL();
      this.periodDialog = false;
    },
    startDateChange() {
      const t = new Date(`${this.period.date}T${this.period.time}`).getTime() / 1000;
      const tNow = moment().unix();
      if (t > tNow) {
        this.period.date = moment.unix(tNow).add('-1', 'hours').utc().format('YYYY-MM-DD');
        this.period.time = moment.unix(tNow).add('-1', 'hours').utc().format('HH:mm');
      }
      if (t < this.earliest) {
        this.period.date = moment.unix(this.earliest).utc().format('YYYY-MM-DD');
        this.period.time = moment.unix(this.earliest).utc().format('HH:mm');
      }
    },
    endDateChange() {
      let t = new Date(`${this.period.endDate}T${this.period.endTime}`).getTime() / 1000;
      const d = new Date();
      t += -d.getTimezoneOffset() * 60;
      const tNow = moment().unix();
      if (t > tNow) {
        this.period.endDate = moment().utc().format('YYYY-MM-DD');
        this.period.endTime = moment().utc().format('HH:mm');
      }
    },
    formatChange() {
      this.updateResultURL();
    },
    jumpToChange() {
      this.origin = 'now';
      if (this.jumpTo === '-1 hour') {
        this.offset = '-60m';
      } else if (this.jumpTo === '-5 hours') {
        this.offset = '-300m';
      } else if (this.jumpTo === '-1 day') {
        this.offset = '-1440m';
      }
      this.updateResultURL();
    },
    updateResultURL() {
      let url = this.record.hls3URL;
      let { mp4URL } = this.record;
      if (this.format === 'HLS5') {
        url = this.record.hls5URL;
      } else if (this.format === 'HLS7') {
        url = this.record.hls7URL;
      } else if (this.format === 'DASH') {
        url = this.record.dashURL;
      }
      url = url.replace('$origin', this.origin);
      url = url.replace('$offset', this.offset);

      let mp4Origin = this.origin;
      let mp4Offset = this.offset;

      const minutes = Number(this.offset.replace('m', ''));
      if (this.origin === 'now') {
        this.download.start = moment().add(minutes, 'minutes').utc().format('YYYY/MM/DD HH:mm');
        this.download.end = moment().utc().format('YYYY/MM/DD HH:mm');
        this.playFrom.start = 'From now to ';
        this.playFrom.duration = Math.abs(minutes) === 60 ? '1 hour back' : `${-minutes / 60} hours back`;
        mp4Origin = moment().utc().add(minutes, 'minutes').format('YYYYMMDDHHmm');
        mp4Offset = `${Math.abs(minutes)}m`;
      } else {
        const t = new Date(`${this.period.date}T${this.period.time}`).getTime() / 1000;
        this.download.start = moment.unix(t).format('YYYY/MM/DD HH:mm');
        this.download.end = moment.unix(t).add(minutes, 'minutes').utc().format('YYYY/MM/DD HH:mm');

        this.playFrom.start = `From ${moment.unix(t).utc().format('YYYY/MM/DD HH:mm')} to ${moment.unix(t).add(minutes, 'minutes').utc().format('YYYY/MM/DD HH:mm')}`;
        this.playFrom.duration = ` (${this.processSec(minutes * 60)})`;
      }

      mp4URL = mp4URL.replace('$origin', mp4Origin);
      mp4URL = mp4URL.replace('$offset', mp4Offset);

      this.resultURL = url;
      this.setFrameSrc(url);
      this.mp4URL = mp4URL;
    },
    findChannelName() {
      const args = {
        limit: 500,
        offset: 0,
      };
      this.$store.dispatch('jobs/getLiveJobs', args)
        .then((jobs) => {
          for (let i = 0; i < jobs.length; i += 1) {
            const job = jobs[i];
            for (let j = 0; j < job.outputs.length; j += 1) {
              const output = job.outputs[j];
              if (output.format === 'segmented_hls' && output.url.length > 0) {
                const parts = this.record.inputURL.split('?hls.variants=');
                if (parts.length === 2
                  && job.profile
                  && job.profile.video_template && job.profile.video_template.streams) {
                  const bitrateParts = parts[1].split(',');
                  const bitrateInxMap = {};
                  for (let k = 0; k < bitrateParts.length; k += 1) {
                    bitrateInxMap[Number(bitrateParts[k])] = true;
                  }
                  const bitrates = [];
                  for (let k = 0; k < job.profile.video_template.streams.length; k += 1) {
                    const stream = job.profile.video_template.streams[k];
                    // eslint-disable-next-line no-prototype-builtins
                    if (bitrateInxMap.hasOwnProperty(k)) {
                      bitrates.push(`${stream.bitrate} Kbps`);
                    }
                  }
                  this.recordingBitrates = bitrates.join(', ');
                }
                if (this.processURL(output.url[0]) === parts[0]) {
                  this.channel = { id: job.id, name: job.name };
                  return;
                }
              }
            }
          }
        })
        .catch((errorMsg) => {
          console.log(errorMsg);
        });
    },
    processURL(url) {
      if (url.startsWith('origin')) {
        return url.replace('origin', 'http');
      }
      return url;
    },
    goToChannel() {
      this.$router.push({
        name: 'LiveTranscodingJobDetailPage',
        params: {
          id: this.channel.id,
        },
      });
    },
    goToList() {
      this.$router.push({ name: 'DVRListPage' });
    },
    createExtURL(url) {
      if (!url) return '';
      const link = url.replace(/^http:\/\//, 'https://');
      return `https://hls-js.netlify.com/demo/?src=${escape(link)}`;
    },
    updateRecord() {
      if (!this.isFormValid) return;

      const dc = DVRChannelFormToJsonAPI(this.form);
      dc.id = this.record.id;

      const action = 'dvr/updateDVRChannel';

      this.$store.dispatch(action, dc)
        .then((result) => {
          this.record.name = result.name;
          this.record.duration = result.duration;
          this.setEditForm();
          this.cancelEditing();
        })
        .catch((errorMsg) => {
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    dayPeriodChanged() {
      if (this.form.duration.days < 0) {
        this.form.duration.days = 0;
      }
    },
    hourPeriodChanged() {
      if (this.form.duration.hours < 0) {
        this.form.duration.hours = 0;
      }
      if (this.form.duration.hours > 23) {
        this.form.duration.hours = 23;
      }
    },
    cancelEditing() {
      this.editDialog = false;
      this.setEditForm();
    },
    setLive() {
      const link = this.record.inputURL.replace(/^http:\/\//, 'https://');
      this.setFrameSrc(link);
      this.jumpTo = '';
      this.playFrom.start = 'Live';
      this.playFrom.duration = '';
    },
    processSec(sec) {
      const duration = moment.duration(sec * 1000);
      let days = Math.floor(duration.asDays());
      if (days < 1) {
        days = 0;
      }
      duration.subtract(moment.duration(days, 'days'));
      let hours = Math.floor(duration.hours());
      if (hours < 1) {
        hours = 0;
      }
      duration.subtract(moment.duration(hours, 'hours'));
      const minutes = Math.floor(duration.minutes());
      if (days > 0) {
        return `${days}d ${hours}h ${minutes}m`;
      } if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    },
    downloadFile() {
      const link = document.createElement('a');
      link.href = this.mp4URL;
      link.setAttribute('download', 'video.mp4');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
